/**
 * Image utility functions for responsive image handling
 */

export interface ResponsiveImageConfig {
  compactWidth?: number; // Image width for ≤600px
  desktopWidth?: number; // Image width for >600px
  sizes?: string; // CSS sizes attribute eg. '(max-width: 600px) 100vw, 50vw'
}

export interface SrcSetConfig {
  src: string;
  srcSet?: string;
  sizes?: string;
}

/**
 * Checks if a URL is from Contentful CDN
 */
export function isContentfulImage(url: string): boolean {
  return url.includes('images.ctfassets.net');
}

/**
 * Adds query parameters to a Contentful image URL
 * Contentful assets api supports the following parameters:
 * - w: width
 * - h: height
 * - fm: format
 * - q: quality
 * - fit: fit
 * - bg: background color
 */
export function addContentfulParams(url: string, width?: number, format = 'webp'): string {
  const urlObj = new URL(url);

  if (width) {
    urlObj.searchParams.set('w', width.toString());
  }

  if (format) {
    urlObj.searchParams.set('fm', format);
  }

  urlObj.searchParams.set('fit', 'fill');

  return urlObj.toString();
}

/**
 * Generates responsive image attributes based on configuration
 * For Contentful images: creates srcset with width and format parameters
 * For other images: returns original URL unchanged
 */
export function generateResponsiveImageAttributes(
  originalUrl: string,
  config: ResponsiveImageConfig,
): SrcSetConfig {
  // For non-Contentful images, just return the original src as-is
  if (!isContentfulImage(originalUrl)) {
    return { src: originalUrl };
  }

  const { compactWidth, desktopWidth, sizes } = config;

  if (!compactWidth && !desktopWidth) {
    return { src: originalUrl };
  }

  // Generate width variants for different screen densities
  const widths: number[] = [];

  if (compactWidth) {
    widths.push(compactWidth);
    widths.push(compactWidth * 2); // Add 2x version for retina displays
  }

  if (desktopWidth && desktopWidth !== compactWidth) {
    widths.push(desktopWidth);
    widths.push(desktopWidth * 2); // Add 2x version for retina displays
  }

  // Remove duplicates and sort
  const uniqueWidths = Array.from(new Set(widths)).sort((a, b) => a - b);

  // Generate srcset entries
  const srcSetEntries = uniqueWidths.map(
    (width) => `${addContentfulParams(originalUrl, width)} ${width}w`,
  );

  // Use the largest width as the default src
  const defaultWidth = Math.max(...uniqueWidths);
  const src = addContentfulParams(originalUrl, defaultWidth);

  return {
    src,
    srcSet: srcSetEntries.join(', '),
    sizes,
  };
}
